// Database-backed FriendsService
// Replaces localStorage with API calls to the social backend

import { API_BASE } from '../config/api';
import AuthService from './authService';

class FriendsServiceDB {
  static API_SOCIAL = `${API_BASE}/api/social`;

  // Use centralized AuthService.handleResponse for auth error handling

  // Send friend request
  static async sendFriendRequest(friendId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends/request`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ friendId })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error sending friend request:', error);
      return { success: false, error: error.message };
    }
  }

  // Accept friend request
  static async acceptFriendRequest(friendId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends/accept`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ friendId })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error accepting friend request:', error);
      return { success: false, error: error.message };
    }
  }

  // Remove friend or decline request
  static async removeFriend(friendId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends/${friendId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error removing friend:', error);
      return { success: false, error: error.message };
    }
  }

  // Block user
  static async blockUser(userId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends/block`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ userId })
      });

      const result = await AuthService.handleResponse(response);
      // Every surface that can block goes through here, so the caches and
      // the cards on screen are cleared once, for all of them (C74). Loaded
      // lazily: cacheInvalidation reaches useHomeDashboard, which imports
      // this file.
      if (result?.success) {
        try {
          const { notifyUserBlocked } = await import('../utils/cacheInvalidation');
          notifyUserBlocked(userId);
        } catch (err) {
          // The block stands; only the local clean-up failed.
          console.error('Error clearing state after a block:', err);
        }
      }
      return result;
    } catch (error) {
      console.error('Error blocking user:', error);
      return { success: false, error: error.message };
    }
  }

  // Unblock user
  static async unblockUser(userId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends/block?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      const result = await AuthService.handleResponse(response);
      // Their content may come back on the next fetch (C74).
      if (result?.success) {
        const { forgetBlockedUser } = await import('../utils/blockedCards');
        forgetBlockedUser(userId);
      }
      return result;
    } catch (error) {
      console.error('Error unblocking user:', error);
      return { success: false, error: error.message };
    }
  }

  // Get blocked users list
  static async getBlockedUsers() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends/blocked`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.blocked || [];
    } catch (error) {
      console.error('Error getting blocked users:', error);
      return [];
    }
  }

  // Get current user's friends
  static async getFriends() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.friends || [];
    } catch (error) {
      console.error('Error getting friends:', error);
      return [];
    }
  }

  // Get pending friend requests
  static async getPendingRequests(type = 'received') {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends/requests?type=${type}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.requests || [];
    } catch (error) {
      console.error('Error getting friend requests:', error);
      return [];
    }
  }

  // Get friend suggestions
  static async getFriendSuggestions() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/friends/suggestions`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.suggestions || [];
    } catch (error) {
      console.error('Error getting friend suggestions:', error);
      return [];
    }
  }

  // === FOLLOWS METHODS ===

  // Follow user
  static async followUser(userId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/follows`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ userId })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error following user:', error);
      return { success: false, error: error.message };
    }
  }

  // Unfollow user
  static async unfollowUser(userId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/follows/${userId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error unfollowing user:', error);
      return { success: false, error: error.message };
    }
  }

  // Get following list
  static async getFollowing() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/follows/following`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.following || [];
    } catch (error) {
      console.error('Error getting following:', error);
      return [];
    }
  }

  // Get followers list
  static async getFollowers() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/follows/followers`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.followers || [];
    } catch (error) {
      console.error('Error getting followers:', error);
      return [];
    }
  }

  // Get follow suggestions
  static async getFollowSuggestions() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/follows/suggestions`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.suggestions || [];
    } catch (error) {
      console.error('Error getting follow suggestions:', error);
      return [];
    }
  }

  // === INTERACTIONS METHODS ===

  // Add interaction (like, save, share)
  static async addInteraction(contentType, contentId, interactionType = 'like', value = 1, metadata = null) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/interactions`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          contentType,
          contentId,
          interactionType,
          value,
          metadata
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error adding interaction:', error);
      return { success: false, error: error.message };
    }
  }

  // Remove interaction
  static async removeInteraction(contentType, contentId, interactionType = 'like') {
    try {
      const response = await fetch(
        `${FriendsServiceDB.API_SOCIAL}/interactions?contentType=${contentType}&contentId=${contentId}&interactionType=${interactionType}`,
        {
          method: 'DELETE',
          headers: AuthService.getJsonHeaders()
        }
      );

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error removing interaction:', error);
      return { success: false, error: error.message };
    }
  }

  // Toggle interaction (like/unlike)
  static async toggleInteraction(contentType, contentId, interactionType = 'like', value = 1) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/interactions/toggle`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          contentType,
          contentId,
          interactionType,
          value
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error toggling interaction:', error);
      return { success: false, error: error.message };
    }
  }

  // === COMMENTS METHODS ===

  // Add comment
  static async addComment(contentType, contentId, text, parentCommentId = null) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/comments`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({
          contentType,
          contentId,
          text,
          parentCommentId
        })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error adding comment:', error);
      return { success: false, error: error.message };
    }
  }

  // Update comment
  static async updateComment(commentId, text) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/comments/${commentId}`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ text })
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error updating comment:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete comment
  static async deleteComment(commentId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/comments/${commentId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error deleting comment:', error);
      return { success: false, error: error.message };
    }
  }

  // The content's owner removes someone else's comment on it. The reply
  // carries `content` ({ content_type, content_id, visibility,
  // comments_allowed }) for the follow-up the comments modal offers.
  static async removeComment(commentId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/comments/${commentId}/remove`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error removing comment:', error);
      return { success: false, error: error.message };
    }
  }

  // Owner only, narrowing only: change is 'friends_only' or 'comments_off'.
  static async narrowCommentAudience(contentType, contentId, change) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/comments/content-settings`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders(),
        body: JSON.stringify({ content_type: contentType, content_id: contentId, change })
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error changing comment settings:', error);
      return { success: false, error: error.message };
    }
  }

  // Pin a comment (creator-only — server enforces ownership)
  static async pinComment(commentId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/comments/${commentId}/pin`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error pinning comment:', error);
      return { success: false, error: error.message };
    }
  }

  static async unpinComment(commentId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/comments/${commentId}/unpin`, {
        method: 'POST',
        headers: AuthService.getJsonHeaders()
      });
      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error unpinning comment:', error);
      return { success: false, error: error.message };
    }
  }

  // Get content comments
  static async getContentComments(contentType, contentId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/comments/${contentType}/${contentId}`, {
        headers: AuthService.getJsonHeaders()
      });
      // 404: the viewer may not see the content (or it is gone). Told apart
      // from a failed load, which the modal keeps on screen and can retry.
      if (response.status === 404) {
        return { comments: [], stats: {}, commentsAllowed: false, failed: true, notFound: true };
      }

      const data = await AuthService.handleResponse(response);
      return {
        comments: data.comments || [],
        stats: data.stats || {},
        // Only an explicit false turns the composer off (older servers omit it).
        commentsAllowed: data.comments_allowed !== false
      };
    } catch (error) {
      console.error('Error getting comments:', error);
      // `failed` lets the comments modal keep the thread it is showing rather
      // than read a network blip as "no comments".
      return { comments: [], stats: {}, commentsAllowed: true, failed: true };
    }
  }

  // === NOTIFICATIONS METHODS ===

  // Get notifications
  static async getNotifications(unreadOnly = false, limit = 50) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/notifications?unreadOnly=${unreadOnly}&limit=${limit}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return {
        notifications: data.notifications || [],
        unreadCount: data.unreadCount || 0
      };
    } catch (error) {
      console.error('Error getting notifications:', error);
      return { notifications: [], unreadCount: 0 };
    }
  }

  // Mark notification as read
  static async markNotificationAsRead(notificationId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return { success: false, error: error.message };
    }
  }

  // Mark all notifications as read
  static async markAllNotificationsAsRead() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/notifications/read-all`, {
        method: 'PUT',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      return { success: false, error: error.message };
    }
  }

  // Delete notification
  static async deleteNotification(notificationId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error deleting notification:', error);
      return { success: false, error: error.message };
    }
  }

  // === COMPATIBILITY METHODS (for backward compatibility) ===

  // Check if users are friends (from existing localStorage service)
  static async areFriends(userId1, userId2) {
    try {
      const friends = await this.getFriends();
      return friends.some(friend => friend.id === userId2 || friend.user_id === userId2);
    } catch (error) {
      console.error('Error checking friendship:', error);
      return false;
    }
  }

  // Get friend count
  static async getFriendCount() {
    try {
      const friends = await this.getFriends();
      return friends.length;
    } catch (error) {
      console.error('Error getting friend count:', error);
      return 0;
    }
  }

  // Legacy method for adding friends (now sends friend request)
  static async addFriend(friendId) {
    return await this.sendFriendRequest(friendId);
  }

  // Get mutual friends (simplified - would need additional API endpoint)
  static async getMutualFriends(userId) {
    // This would require a new API endpoint to efficiently calculate mutual friends
    // For now, return empty array
    console.warn('getMutualFriends not yet implemented with database backend');
    return [];
  }

  // Search users by name or email
  static async searchUsers(query, excludeCurrentUser = true) {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/users/search?query=${encodeURIComponent(query)}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      console.log('🔍 Frontend received search results:', data.users?.length || 0, 'users');
      return data.users || [];
    } catch (error) {
      console.error('Error searching users:', error);
      return [];
    }
  }

  // Get friend status (mock)
  static getFriendStatus(friendId) {
    const statuses = ['online', 'away', 'offline'];
    return statuses[Math.floor(Math.random() * statuses.length)];
  }

  // Unified feed endpoint — single ranked feed for all tabs
  static async getFeed({ scope = 'all', sort = 'ranked', contentType = 'all',
                          search, scenario, lat, lng, limit = 20, cursor,
                          dateStart, dateEnd, dateRanges, holidayAnchors, region, city, country,
                          tourSubtype, countRegionSearch = true, radiusKm,
                          tripAware, discoveryBand,
                          who, companionUserIds, companionChildIds, discoveryRadiusKm, center, contentTypes,
                          trialSlots, world, textOnly } = {}) {
    try {
      const params = new URLSearchParams({ scope, sort, contentType, limit: limit.toString() });
      if (search?.trim()) params.append('search', search.trim());
      if (scenario) params.append('scenario', scenario);
      if (tripAware) params.append('tripAware', '1');
      if (discoveryBand) params.append('discoveryBand', discoveryBand);
      // Discover's Who's going and How far (docs/plans/DISCOVER_REDESIGN.md).
      if (who === 'me') params.append('who', 'me');
      if (Array.isArray(companionUserIds) && companionUserIds.length > 0) {
        params.append('companionUserIds', companionUserIds.join(','));
      }
      if (Array.isArray(companionChildIds) && companionChildIds.length > 0) {
        params.append('companionChildIds', companionChildIds.join(','));
      }
      if (discoveryRadiusKm) params.append('discoveryRadiusKm', String(discoveryRadiusKm));
      if (center) params.append('center', center);
      // With contentType=selected: Discover's multi-select Type.
      if (contentTypes) params.append('contentTypes', contentTypes);
      // Discover's Trip ideas shelf: the new-creator trial's slots.
      if (trialSlots) params.append('trialSlots', '1');
      // Discover's best-of-the-world slot.
      if (world) params.append('world', '1');
      // The search is the words: never swapped for a region of that name.
      if (textOnly && search?.trim()) params.append('textOnly', '1');
      if (lat != null) params.append('lat', lat.toString());
      if (lng != null) params.append('lng', lng.toString());
      if (cursor) params.append('cursor', cursor);
      if (dateStart) params.append('dateStart', dateStart);
      if (dateEnd) params.append('dateEnd', dateEnd);
      // Only send dateRanges when there's more than one range — for the common
      // single-range case dateStart/dateEnd are enough and keep the URL short.
      if (Array.isArray(dateRanges) && dateRanges.length > 1) {
        params.append('dateRanges', JSON.stringify(dateRanges));
      }
      // Holiday anchor keys — the server resolves them to theme keywords and
      // returns only events about those holidays. Keys, not keywords, so the
      // vocabulary stays in one place server-side.
      if (Array.isArray(holidayAnchors) && holidayAnchors.length > 0) {
        params.append('holidayAnchors', holidayAnchors.join(','));
      }
      if (region) params.append('region', region);
      // Membership filters via content_geo_map — city pages ask by geonameid,
      // country pages by ISO alpha-2. Mutually exclusive with region
      // server-side (city/country win).
      if (city != null) params.append('city', String(city));
      if (country) params.append('country', country);
      if (tourSubtype && tourSubtype !== 'all') params.append('tourSubtype', tourSubtype);
      // Hard geographic filter for city destination pages. Only meaningful
      // alongside lat/lng — the server ignores it without a centre.
      if (radiusKm != null && ((lat != null && lng != null) || center === 'home')) {
        params.append('radiusKm', radiusKm.toString());
      }
      // The destination page filters by region for display, not because anyone
      // searched for it — opt out so it doesn't inflate regions.search_count by
      // one-per-section on every page view.
      if (region && !countRegionSearch) params.append('countRegionSearch', 'false');
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/feed?${params}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return { feed: data.feed || [], hasMore: data.hasMore || false, cursor: data.cursor || null };
    } catch (error) {
      console.error('Error getting unified feed:', error);
      // `error` is additive — existing callers destructure feed/hasMore/cursor
      // and are unaffected. It exists because an empty feed and a FAILED feed
      // are not the same thing: without this a network blip is indistinguishable
      // from "no content here", which on the destination page renders as
      // "Nothing here for Alsace yet" and then gets cached.
      return { feed: [], hasMore: false, cursor: null, error: error?.message || true };
    }
  }

  // Get feed stats for current user
  static async getFeedStats() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/feed/stats`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.stats || { friends: 0, following: 0, activities: 0, trips: 0 };
    } catch (error) {
      console.error('Error getting feed stats:', error);
      return { friends: 0, following: 0, activities: 0, trips: 0 };
    }
  }

  // Get recent activity - now calls the social feed API
  static async getRecentActivity(limit = 20) {
    const result = await this.getSocialFeed('discover', limit, 0, 'all');
    return result.feed;
  }

  // Toggle like on content
  static async toggleLike(contentType, contentId) {
    return await this.toggleInteraction(contentType, contentId, 'like');
  }

  // Toggle save on content
  static async toggleSave(contentType, contentId) {
    return await this.toggleInteraction(contentType, contentId, 'save');
  }

  // Get trending destinations for the feed
  static async getTrendingDestinations() {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/feed/trending`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.trending || [];
    } catch (error) {
      console.error('Error getting trending destinations:', error);
      return [];
    }
  }

  // Get search suggestions (for autocomplete)
  static async getSearchSuggestions(query) {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/feed/suggestions?query=${encodeURIComponent(query)}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.suggestions || [];
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return [];
    }
  }

  // Get similar suggestions for "did you mean" (when no results found)
  static async getSimilarSuggestions(query) {
    try {
      if (!query || query.length < 2) {
        return [];
      }

      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/feed/similar?query=${encodeURIComponent(query)}`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data.similar || [];
    } catch (error) {
      console.error('Error getting similar suggestions:', error);
      return [];
    }
  }

  // === ACTIVITY DETAIL METHODS ===

  // Get activity details for the Activity Detail page
  static async getActivityDetails(activityId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/activities/${activityId}/details`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting activity details:', error);
      return { success: false, error: error.message };
    }
  }

  // Get community insights for a place
  static async getPlaceCommunityData(placeId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/places/${encodeURIComponent(placeId)}/community`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting place community data:', error);
      return { success: false, error: error.message };
    }
  }

  // === ALBUM DETAIL METHODS ===

  // Get album details for the Album Detail page
  static async getAlbumDetails(albumId) {
    try {
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/albums/${albumId}/details`, {
        headers: AuthService.getJsonHeaders()
      });

      const data = await AuthService.handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error getting album details:', error);
      return { success: false, error: error.message };
    }
  }

  // === USER PROFILE METHODS ===

  // Get user profile (public or friend view).
  // viewSource ('handle' | 'direct' | 'preview') tells the backend how the
  // viewer arrived, for storefront view analytics — 'handle' means a shared
  // /@handle bio link; 'preview' (never counted) is the owner's
  // preview-as-visitor mode.
  // skipAuth sends the request anonymously — the endpoint is public-mounted,
  // so this returns exactly what a logged-out stranger would see (used by
  // preview-as-visitor for a privacy-accurate rendering).
  static async getUserProfile(userId, viewSource, { skipAuth = false, includeMap = true } = {}) {
    try {
      const params = new URLSearchParams();
      if (viewSource) params.set('view_source', viewSource);
      if (!includeMap) params.set('include_map', 'false');
      // .toString(), never .size — iOS 16 has no URLSearchParams.size and
      // the build ships no polyfill (see followingService.getBands).
      const qsBody = params.toString();
      const qs = qsBody ? `?${qsBody}` : '';
      const response = await fetch(`${FriendsServiceDB.API_SOCIAL}/users/${userId}/profile${qs}`, {
        headers: skipAuth ? { 'Content-Type': 'application/json' } : AuthService.getJsonHeaders()
      });

      return await AuthService.handleResponse(response);
    } catch (error) {
      console.error('Error getting user profile:', error);
      return { success: false, error: error.message };
    }
  }
}

export default FriendsServiceDB;